import { getStoredAuthToken } from 'src/auth/authToken'
import toCamelCase from 'camelcase-keys'
import config from 'shared/config'
import toSnakeCase from 'snakecase-keys'
import { HttpStatusCodes } from 'shared/httpStatus'

const API = config('NEXT_PUBLIC_API_URL') as string

const DEFAULT_SERVER_ERROR_CODE = 500

const STATUS_WITHOUT_BODY = new Set([
  HttpStatusCodes.NO_CONTENT,
  HttpStatusCodes.RESET_CONTENT,
  HttpStatusCodes.NOT_MODIFIED
])

export interface ClientError {
  message: string
  status_code: number
  code?: string
  traceId?: string
  details?: object
  endpoint?: string
}

export class ApiClientError extends Error {
  status_code: number
  code: string | undefined
  message: string
  details: object | undefined
  endpoint: string | undefined

  constructor(error: ClientError) {
    super(error.message)
    this.code = error.code
    this.message = error.message
    this.status_code = error.status_code
    this.details = error.details
    this.endpoint = error.endpoint
  }
}

export interface ClientArgs {
  method: string
  path: string
  data?: object
  token?: string
  rawResponse?: boolean
  preserveCaseKeys?: Array<string | RegExp>
}

interface CustomClientArgs {
  method: string
  data?: object
  path: string
}
const grabTokenFromStore = (): string | undefined => {
  if (typeof window !== 'undefined') {
    return getStoredAuthToken()
  }
}

interface TBaseClientArgs {
  path: string
  method: string
  data?: object
  token?: string
  rawResponse?: boolean
  preserveCaseKeys?: Array<string | RegExp>
}

export interface IClient {
  get: <T>(args: Omit<TBaseClientArgs, 'method'>) => Promise<T>
  post: <T>(args: Omit<TBaseClientArgs, 'method'>) => Promise<T>
  patch: <T>(args: Omit<TBaseClientArgs, 'method'>) => Promise<T>
  put: <T>(args: Omit<TBaseClientArgs, 'method'>) => Promise<T>
  del: <T>(args: Omit<TBaseClientArgs, 'method'>) => Promise<T>
}

type IGetCustomArgs = (args: TBaseClientArgs) => {
  headers: Record<string, string>
  data: object | null
  path: string
  responseHandler?: {
    json: <T>(json: T) => T
    text: <T>(text: T) => T
    blob: <T>(blob: T) => T
  }
}
export function baseClient(config: globalThis.RequestInit, getCustomArgs?: IGetCustomArgs): IClient {
  const api = async <DataType>(args: TBaseClientArgs): Promise<DataType> => {
    const customArgs = getCustomArgs?.(args)
    const body = customArgs?.data != null ? JSON.stringify({ ...customArgs.data }) : (args.data as BodyInit)
    const headers = {
      ...(config?.headers as Record<string, string> | undefined),
      ...customArgs?.headers
    }
    const response = await fetch(customArgs?.path ?? args.path, {
      method: args.method,
      headers,
      ...(args.data != null
        ? {
            body
          }
        : { body: null })
    }).catch((error) => {
      throw new ApiClientError({
        message: error?.message,
        status_code: DEFAULT_SERVER_ERROR_CODE,
        endpoint: args.path
      })
    })
    switch (response.headers.get('content-type')) {
      case 'application/json': {
        // Handle empty responses (e.g., 204 No Content)
        if (STATUS_WITHOUT_BODY.has(response.status)) {
          return {} as unknown as DataType
        }

        const json = await response.json()
        if (json.error != null) {
          const errorMessage = typeof json.error === 'string' ? json.error : (json.error?.message as string | undefined)
          throw new ApiClientError({
            message: errorMessage,
            code: typeof json.error === 'object' ? json.error?.code : undefined,
            status_code: response.status,
            details: typeof json.error === 'object' ? toCamelCase(json.error?.details, { deep: true }) : undefined,
            endpoint: args.path
          })
        }
        return customArgs?.responseHandler?.json<DataType>(json) ?? json
      }
      case 'application/octet-stream': {
        const blob = (await response.blob()) as unknown as DataType
        return customArgs?.responseHandler?.blob(blob) ?? blob
      }
      default: {
        const text = (await response.text()) as unknown as DataType
        return customArgs?.responseHandler?.text?.(text) ?? text
      }
    }
  }

  return {
    get: async <T>(args: Omit<CustomClientArgs, 'method'>) => await api<T>({ ...args, method: 'GET' }),
    post: async <T>(args: Omit<CustomClientArgs, 'method'>) => await api<T>({ ...args, method: 'POST' }),
    patch: async <T>(args: Omit<CustomClientArgs, 'method'>) => await api<T>({ ...args, method: 'PATCH' }),
    put: async <T>(args: Omit<CustomClientArgs, 'method'>) => await api<T>({ ...args, method: 'PUT' }),
    del: async <T>(args: Omit<CustomClientArgs, 'method'>) => await api<T>({ ...args, method: 'DELETE' })
  }
}

export default function client(requestConfig?: globalThis.RequestInit, options?: { baseUrl?: string }): IClient {
  const defaultConfig = {
    headers: { 'content-type': 'application/json' }
  }
  const baseConfig = {
    ...defaultConfig,
    ...requestConfig
  }
  // Defer the API fallback until request time (avoids TDZ under circular imports in tests).
  const overrideBaseUrl = options?.baseUrl

  interface TGetCustomArgs {
    headers: Record<string, string>
    data: object | null
    path: string
    responseHandler?: any
  }
  const getCustomArgs = (args: ClientArgs): TGetCustomArgs => {
    const token = args.token != null ? args.token : grabTokenFromStore()
    const headers: Record<string, string> = { ...defaultConfig.headers }
    if (token != null) {
      headers.Authorization = `Bearer ${token}`
    }

    return {
      path: `${overrideBaseUrl ?? API}${args.path}`,
      headers,
      data:
        args?.data != null
          ? {
              ...toSnakeCase(args.data, {
                deep: true,
                ...(args.preserveCaseKeys != null && { exclude: args.preserveCaseKeys })
              })
            }
          : null,
      responseHandler: {
        json: (response: any) => {
          // Return raw response if flag is true
          if (args.rawResponse === true) {
            return toCamelCase(response, {
              deep: true,
              ...(args.preserveCaseKeys != null && { exclude: args.preserveCaseKeys })
            })
          }
          // Default behavior: return only the data part
          return toCamelCase(response.data, {
            deep: true,
            ...(args.preserveCaseKeys != null && { exclude: args.preserveCaseKeys })
          })
        }
      }
    }
  }

  return baseClient(baseConfig, getCustomArgs)
}
